import { Flex } from 'antd';

import { ProgressBar } from '@suntex.fim/ui';

import { IndexChartFrame } from './IndexChartFrame';
import { CHART_DIMENSIONS, algebraGoalsIndexSectors } from './const';
import {
  AlgebraGoalsIndexChartViewProps,
  AlgebraGoalsIndexData,
} from './types';
import { useRaphaelChart } from './useRaphaelChart';

// Default description for hover
const defaultDescription =
  'The algebra goals metric is an overall measure of performance on First in Math in eight algebraic categories. Hover over the sectors for more information.';

const PERCENTAGE_MULTIPLIER = 100;

/**
 * Presentational Algebra Goals Index chart component using Raphael.js.
 * This component renders the chart given pre-fetched data.
 * For Storybook and testing purposes.
 */
export function AlgebraGoalsIndexChartView({
  data,
  expanded,
  showLegend,
  onChartClick,
  showProgressBars = false,
}: AlgebraGoalsIndexChartViewProps): JSX.Element {
  // Use the shared Raphael chart hook
  const { paperRef, sectorIndex: descriptionIndex } = useRaphaelChart({
    dimensions: CHART_DIMENSIONS,
    sectors: algebraGoalsIndexSectors,
    data: data as AlgebraGoalsIndexData,
    expanded,
  });

  // Get description for hovered sector
  const description =
    descriptionIndex >= 0
      ? algebraGoalsIndexSectors[descriptionIndex]?.description ||
        defaultDescription
      : defaultDescription;

  const sectorTitle = algebraGoalsIndexSectors[descriptionIndex]?.name || '';

  const chartFrame = IndexChartFrame({
    paperRef,
    text: {
      title: 'First in Math Algebra Goals Metric',
      sectorTitle,
      description,
    },
    showLegend,
    onChartClick,
  });

  if (!showProgressBars) {
    return chartFrame;
  }

  return (
    <Flex vertical gap="20px">
      {chartFrame}
      {algebraGoalsIndexSectors.map((sector) => {
        return (
          <ProgressBar
            key={sector.scorePropertyName}
            label={sector.name}
            percent={
              (data[sector.scorePropertyName] / sector.weight) *
              PERCENTAGE_MULTIPLIER
            }
            fill="primary"
            showData={false}
          />
        );
      })}
    </Flex>
  );
}
